import { useRef, useState } from 'react';
import { TrendingUp, Lightbulb, PenLine, Info } from 'lucide-react';
import ChaseHi from '../../assets/images/ChaseHi.png';
import ChaseThink from '../../assets/images/ChaseThink.png';
import ChasePopover from './ChasePopover';
import { palette } from '../pricing/pricingTokens';
import haptics from '../../utils/haptics';

/**
 * Chase, at rest, on card detail.
 *
 * He is also the loading state for pricing. Standing there saying he is
 * researching is a better wait than a spinner over an empty box, and it costs
 * nothing because he has to be on screen anyway.
 *
 * Tapping him opens a popover, not a panel. The questions are a menu: you pick
 * one and leave. Growing the page to hold them pushed the price ladder down
 * under a thumb that was already moving.
 *
 * The three questions are how a conversation starts, so they only appear when
 * there is no conversation. Once this card has a thread, tapping him reopens it.
 * Being asked to choose a door you already walked through is the kind of thing
 * that makes an assistant feel like a form.
 */

const DOORS = [
  { key: 'pricing', label: 'Dive into pricing', Icon: TrendingUp },
  { key: 'facts', label: 'Interesting facts', Icon: Lightbulb },
  { key: 'custom', label: 'Ask something else', Icon: PenLine },
];

export default function AskChase({ ready, hasThread, onPick, onResume, onComingSoon, theme, isDark = true }) {
  const c = palette(theme, isDark);
  const s = styles(c);

  const cardRef = useRef(null);
  const infoRef = useRef(null);
  const [menu, setMenu] = useState(null); // 'doors' | 'learn' | null

  const tapChase = () => {
    if (!ready) return;
    haptics.tap();
    if (hasThread) { onResume(); return; }
    setMenu((m) => (m === 'doors' ? null : 'doors'));
  };

  return (
    <>
      <div
        ref={cardRef}
        role="button"
        tabIndex={ready ? 0 : -1}
        aria-disabled={!ready}
        aria-haspopup={hasThread ? undefined : 'menu'}
        aria-expanded={hasThread ? undefined : menu === 'doors'}
        style={{ ...s.card, ...(menu === 'doors' ? s.cardOpen : {}), ...(ready ? {} : s.cardWaiting) }}
        onClick={tapChase}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tapChase(); }
        }}
      >
        <img src={ready ? ChaseHi : ChaseThink} alt="Chase" style={s.art} />
        <span style={s.name}>Ask Chase</span>
        <span style={s.state}>
          {ready ? (hasThread ? 'Pick up where you left off' : 'Pricing, facts, anything') : (
            <>
              <span style={s.dots}>
                <i style={{ ...s.dot, animationDelay: '0ms' }} />
                <i style={{ ...s.dot, animationDelay: '180ms' }} />
                <i style={{ ...s.dot, animationDelay: '360ms' }} />
              </span>
              Researching pricing
            </>
          )}
        </span>

        {/* An icon and nothing else. What it explains lives in its own popover. */}
        <button
          ref={infoRef}
          type="button"
          style={s.info}
          aria-label="What Chase learns"
          aria-haspopup="menu"
          aria-expanded={menu === 'learn'}
          onClick={(e) => {
            e.stopPropagation();
            haptics.tap();
            setMenu((m) => (m === 'learn' ? null : 'learn'));
          }}
        >
          <Info size={15} />
        </button>
      </div>

      <ChasePopover
        anchorRef={cardRef}
        open={menu === 'doors'}
        onClose={() => setMenu(null)}
        theme={theme}
        isDark={isDark}
      >
        {DOORS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            role="menuitem"
            style={s.door}
            onClick={() => { haptics.tap(); setMenu(null); onPick(key); }}
          >
            <Icon size={16} color={c.link} />
            {label}
          </button>
        ))}
      </ChasePopover>

      <ChasePopover
        anchorRef={infoRef}
        open={menu === 'learn'}
        onClose={() => setMenu(null)}
        theme={theme}
        isDark={isDark}
      >
        <div style={s.learn}>
          Chase learns what you are usually interested in so his answers get better.
        </div>
        <button
          type="button"
          role="menuitem"
          style={{ ...s.door, ...s.learnAction }}
          onClick={() => { haptics.tap(); setMenu(null); onComingSoon(); }}
        >
          Update your preferences
        </button>
      </ChasePopover>
    </>
  );
}

const styles = (c) => ({
  card: {
    position: 'relative', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: '3px',
    background: c.card, border: `0.5px solid ${c.hairline}`, borderRadius: '13px',
    padding: '8px 8px 10px', cursor: 'pointer', width: '100%', height: '100%',
    color: 'inherit', fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
  },
  cardOpen: { borderColor: c.blue, background: c.lifted },
  cardWaiting: { cursor: 'default' },

  art: { height: '52px', width: 'auto', display: 'block', marginBottom: '1px' },
  name: { fontSize: '13px', fontWeight: '600', color: c.white, lineHeight: 1.2 },
  state: {
    fontSize: '10.5px', color: c.secondary, display: 'flex', alignItems: 'center',
    gap: '5px', justifyContent: 'center', lineHeight: 1.35,
  },

  dots: { display: 'inline-flex', gap: '3px', alignItems: 'center' },
  dot: {
    width: '3.5px', height: '3.5px', borderRadius: '50%', background: c.secondary,
    display: 'block', animation: 'cc-pulse 1.3s ease-in-out infinite',
  },

  info: {
    position: 'absolute', top: '5px', right: '5px', border: 0, background: 'transparent',
    color: c.faint, padding: '5px', display: 'inline-flex', borderRadius: '50%',
    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },

  door: {
    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
    background: 'transparent', border: 0, borderRadius: '9px', padding: '11px 11px',
    fontFamily: 'inherit', fontSize: '14px', color: c.primary, cursor: 'pointer',
    textAlign: 'left', WebkitTapHighlightColor: 'transparent',
  },
  learn: {
    fontSize: '12.5px', lineHeight: 1.5, color: c.secondary, padding: '9px 11px 7px',
  },
  learnAction: {
    color: c.link, borderTop: `0.5px solid ${c.divider}`, borderRadius: 0,
    marginTop: '2px', paddingTop: '11px',
  },
});
